import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserData from '../../services/UserData';
import ENV from '../../services/Environment';
import ManualPreviewService from '../../services/ManualPreviewService';
import WhiteMainBackground from '../../shared_components/WhiteMainBackground/WhiteMainBackground';
import MainHeading from '../../shared_components/MainHeading/MainHeading';
import AssetViewWithTracking from '../../shared_components/AssetView/AssetViewWithTracking';
import Spinner from '../../shared_components/Spinner/Spinner';
import BackgroundButtons from '../AssetPreview/BackgroundButtons/BackgroundButtons';
import previewStyle from '../AssetPreview/AssetPreview.module.css';
import style from './AssetTracking.module.css';

const cleanUpPreview = (session) => {
  const preview = session.preview;
  session.preview = null;
  if (preview?.guid) {
    ManualPreviewService.deletePreview(preview.userData, preview.guid).catch(error => {
      console.error('Asset testing preview cleanup failed:', error);
    });
  }
};

const AssetTracking = () => {
  const userData = useContext(UserData);
  const navigate = useNavigate();
  const sessionRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [background, setBackground] = useState(ENV.appIconName);

  useEffect(() => {
    // Each mounted session owns its upload, including a response arriving after navigation.
    const session = { active: true, uploading: false, preview: null };
    sessionRef.current = session;
    return () => {
      session.active = false;
      cleanUpPreview(session);
    };
  }, []);

  const uploadZip = async (file) => {
    const session = sessionRef.current;
    if (!file || session.uploading) return;
    if (!/\.zip$/i.test(file.name)) {
      setError('Please select a ZIP file.');
      return;
    }

    session.uploading = true;
    setUploading(true);
    setError('');
    try {
      const information = await ManualPreviewService.upload(userData, file);
      session.preview = { guid: information?.guid, userData };
      if (!session.active) {
        cleanUpPreview(session);
        return;
      }
      if (!information?.ok || !information.location || !information.guid) {
        cleanUpPreview(session);
        throw new Error('The ZIP file could not be previewed. Please try another file.');
      }
      setPreview({ ...information, assetName: file.name });
    } catch (uploadError) {
      if (session.active) setError(uploadError.message || 'Unable to upload the ZIP file.');
    } finally {
      session.uploading = false;
      if (session.active) setUploading(false);
    }
  };

  const selectZip = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    uploadZip(file);
  };

  const dropZip = (event) => {
    event.preventDefault();
    setDragging(false);
    if (sessionRef.current.uploading) return;
    const files = event.dataTransfer.files;
    if (files.length > 1) {
      setError('Please upload one ZIP file at a time.');
      return;
    }
    uploadZip(files[0]);
  };

  const goBack = () => {
    if (preview) {
      cleanUpPreview(sessionRef.current);
      setPreview(null);
      setBackground(ENV.appIconName);
    } else {
      navigate('/plugins');
    }
  };

  return (
    <WhiteMainBackground>
      <div className={previewStyle.main}>
        <MainHeading backButtonAction={goBack}>
          {preview ? preview.assetName : 'Asset Testing'}
        </MainHeading>
        {preview ? (
          <>
            <AssetViewWithTracking styleName={background} url={preview.location} title={preview.assetName} />
            <BackgroundButtons changeBackground={setBackground} />
          </>
        ) : (
          <div
            className={`${style.upload} ${dragging ? style.dragging : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = uploading ? 'none' : 'copy';
              setDragging(!uploading);
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
            }}
            onDrop={dropZip}
          >
            <label htmlFor="asset-tracking-zip">Upload an HTML5 asset ZIP file</label>
            <p>Drag and drop a ZIP file here, or choose a file to preview its HTML content.</p>
            <input
              id="asset-tracking-zip"
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              onChange={selectZip}
              disabled={uploading}
            />
            {uploading && <div role="status"><Spinner />Uploading ZIP file…</div>}
            {error && <p role="alert" className={style.error}>{error}</p>}
          </div>
        )}
      </div>
    </WhiteMainBackground>
  );
};

export default AssetTracking;
